import React, { useRef, useState } from "react";
import { ScrollView, TouchableOpacity } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { useNavigation } from "@react-navigation/native";
import styled from "styled-components/native";
import Popup from "@components/molecules/PopUp";
import {
  navigateToHistory,
  showNavigationConfirmation,
} from "@redux/slices/navigationHistorySlice";
import { useTranslation } from "react-i18next";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { messageType } from "../PopUp/types";
import { SpaceType } from "@components/atoms/Spacer/types";
import { VDBText } from "@components/atoms";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import { BreadCrumbLabel } from "./utils";
import { setBuilderProduct } from "@redux/slices/appStateSlice";
import Routes from "@navigations/Routes";
import { ProductCategory } from "@utils/constants";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import {
  popToScreen,
  refreshSavedSearchForEntry,
} from "@scenes/jewelry-builder/utils/utils";
import { css } from "styled-components/native";
import { setProductType } from "@redux/slices/appStateSlice";
import {
  setSectionName,
  useSearchFiltersSlice,
} from "@redux/slices/searchFilters";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { getSectionName } from "@utils/product/searchResultsNavigation";
import { goBack } from "@navigations/utils/RootNavigation";
import { DefaultTheme } from "styled-components/native";

interface NavigationState {
  screen: string;
  params?: any;
}

interface NavigationHistoryState {
  history: NavigationState[];
  currentIndex: number;
  showConfirmationDialog: boolean;
  targetNavigationIndex: number | null;
}

const Breadcrumbs = ({}: any) => {
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const { t } = useTranslation();
  const { typography, theme } = useScreenOptionsHooks();
  const [showPopUp, setShowPopUp] = useState(false);

  const { history, currentIndex, targetNavigationIndex } = useSelector(
    (state: any) => state.navigationHistory as NavigationHistoryState
  );

  const {
    resetDefaultSlug,
    setItemDetail,
    setStoneItemDetail,
    setMatchingPairItemDetail,
    stoneItemDetail,
    itemDetail,
  } = useJewelryConfiguratorState();
  const { min_max_Options, auxPayload } = useSearchFiltersSlice();
  const userInfo = useUserInfo();
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const modifyShapeSupportForBuilder =
    min_max_Options?.supported_shapes?.length > 0 &&
    userInfo.user.feature_settings?.allow_search_modify_in_builder &&
    enableJewelryBuilder &&
    !!stoneItemDetail &&
    stoneItemDetail?.type === ProductCategory.JEWELRY &&
    !!itemDetail &&
    stoneItemDetail?.id !== itemDetail?.id &&
    history.length > 2;
  const scrollViewRef = useRef<ScrollView>(null);

  const getLabels = (type?: BreadCrumbLabel) => {
    switch (type) {
      case BreadCrumbLabel.STONE_SEARCH:
        return t("reviewPage.breadCrumbs.stone.search.text");
      case BreadCrumbLabel.STONE_DETAILS:
        return t("reviewPage.breadCrumbs.stone.details.text");
      case BreadCrumbLabel.SETTING_SEARCH:
        return t("reviewPage.breadCrumbs.setting.search.text");
      case BreadCrumbLabel.SETTING_DETAILS:
        return t("reviewPage.breadCrumbs.setting.details.text");
      case BreadCrumbLabel.REVIEW:
        return t("reviewPage.breadCrumbs.review.text");
      case BreadCrumbLabel.REQUEST_DETAILS:
        return t("reviewPage.breadCrumbs.requestDetails.text");
      case BreadCrumbLabel.ORDER_CART:
        return t("reviewPage.breadCrumbs.orderCart.text");
      case BreadCrumbLabel.MATCHING_STONE:
        return t("reviewPage.breadCrumbs.matchingStone.text");
      default:
        return t("reviewPage.breadCrumbs.review.text");
    }
  };

  const handleNavigate = (index: number) => {
    if (history.length > 2 && index < currentIndex) {
      dispatch(showNavigationConfirmation(index));
      setShowPopUp(true);
    } else {
      dispatch(navigateToHistory(index));
      const targetState = history[index];
      if (
        targetState.params.breadCrumbLabel ===
          BreadCrumbLabel.REQUEST_DETAILS ||
        targetState.params.breadCrumbLabel === BreadCrumbLabel.ORDER_CART
      ) {
        goBack();
      } else {
        const updatedHistory = history.map((item, idx) => {
          let updatedItem = item;
          if (
            item.screen === Routes.FilterSplitScreen &&
            item.params.productType === ProductCategory.JEWELRY
          ) {
            updatedItem = {
              ...updatedItem,
              params: {
                ...updatedItem.params,
                filterSplitStep: 2,
              },
            };
          }
          if (idx === index) {
            updatedItem = refreshSavedSearchForEntry(updatedItem, auxPayload);
          }
          return updatedItem;
        });

        popToScreen(
          navigation,
          updatedHistory,
          targetState.screen,
          targetState.params?.productType,
          theme,
          modifyShapeSupportForBuilder
        );
      }
    }
  };

  const handleConfirmNavigation = () => {
    if (targetNavigationIndex !== null) {
      dispatch(navigateToHistory(targetNavigationIndex));
      const targetState = history[targetNavigationIndex];
      const relevantHistory = history.slice(0, targetNavigationIndex + 1);
      const pdpItems = relevantHistory.filter(
        (entry) => entry.screen === Routes.PDPScreen
      );
      if (pdpItems.length === 1) {
        const pdpProductType = pdpItems[0]?.params?.productType;
        if (
          pdpProductType !== ProductCategory.JEWELRY ||
          pdpProductType !== ProductCategory.LABGROWN_JEWELRY
        ) {
          setItemDetail({} as any);
        } else {
          setStoneItemDetail({} as any);
          setMatchingPairItemDetail({} as any);
        }
      } else if (pdpItems.length === 0) {
        resetDefaultSlug(true);
        setItemDetail({} as any);
        setStoneItemDetail({} as any);
        setMatchingPairItemDetail({} as any);
      }
      if (targetState.params?.productType) {
        dispatch(setBuilderProduct(targetState.params?.productType));
        dispatch(setProductType(targetState.params?.productType));

        const sectionName = getSectionName(targetState.params?.productType);

        dispatch(setSectionName(sectionName));
      }

      const updatedHistory = history.map((item, index) => {
        let updatedItem = item;
        if (
          index === targetNavigationIndex &&
          item.screen === Routes.FilterSplitScreen &&
          item.params.productType === ProductCategory.JEWELRY
        ) {
          updatedItem = {
            ...updatedItem,
            params: {
              ...updatedItem.params,
              filterSplitStep: 2,
            },
          };
        }
        if (index === targetNavigationIndex) {
          updatedItem = refreshSavedSearchForEntry(updatedItem, auxPayload);
        }
        return updatedItem;
      });

      popToScreen(
        navigation,
        updatedHistory,
        targetState.screen,
        targetState.params?.productType,
        theme,
        modifyShapeSupportForBuilder
      );
    }
    setShowPopUp(false);
  };

  const handleCancelNavigation = () => {
    setShowPopUp(false);
  };

  if (history.length < 2) return null;

  return (
    <>
      <ScrollView
        ref={scrollViewRef}
        horizontal={true}
        showsHorizontalScrollIndicator={false}
        scrollEnabled={theme.isMobile}
        style={
          theme.isMobile
            ? {
                flexGrow: 0,
                paddingTop: SpaceType.Basic,
                paddingBottom: SpaceType.Basic,
                paddingHorizontal: SpaceType.Double,
                backgroundColor: theme["core-01"],
                borderBottomColor: theme["core-03"],
                borderBottomWidth: 1,
              }
            : { flexGrow: 0, margin: SpaceType.Double }
        }
      >
        <Container
          onLayout={() => {
            if (scrollViewRef.current) {
              scrollViewRef.current.scrollToEnd({ animated: false });
            }
          }}
        >
          {history.map((item: NavigationState, index: number) => (
            <React.Fragment key={index}>
              {index > 0 && (
                <Separator>
                  <VDBIcon
                    variant={IconsVariant.VDBCustom}
                    name={VDBIcons.ARROW_RIGHT}
                    size={16}
                    color={theme["core-05"]}
                  />
                </Separator>
              )}
              <BreadcrumbTouchable
                onPress={() => handleNavigate(index)}
                $isActive={index === currentIndex}
              >
                <VDBText
                  color={
                    index === currentIndex
                      ? theme["option-cont-active"]
                      : theme["option-cont"]
                  }
                  type={
                    index === currentIndex
                      ? typography["Minor-M"]
                      : typography["Minor-M"]
                  }
                >
                  {t(`${getLabels(item.params.breadCrumbLabel)}`)}
                </VDBText>
              </BreadcrumbTouchable>
            </React.Fragment>
          ))}
        </Container>
      </ScrollView>
      {showPopUp && (
        <Popup
          type={messageType.Generic}
          buttons={[
            {
              type: ButtonType.primary,
              title: t("reviewPage.stone.details.popUp.buttons.yes.text"),
              onPressAction: handleConfirmNavigation,
              category: ButtonCategory.default,
            },
            {
              type: ButtonType.secondary,
              title: t("reviewPage.stone.details.popUp.buttons.cancel.text"),
              onPressAction: handleCancelNavigation,
            },
          ]}
          HtmlSource={{
            html: t("reviewPage.stone.details.popUp.title.text"),
          }}
          styleKey="Body-R"
        />
      )}
    </>
  );
};

const Container = styled.View`
  flex-direction: row;
  align-items: center;
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isLargeScreen ? theme["core-01"] : theme["core-02"]};
  ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile &&
    css`
      padding-right: ${SpaceType.large}px;
      height: ${SpaceType.large}px;
    `}
`;

const Separator = styled.View`
  margin: 0 4px;
`;

const BreadcrumbTouchable = styled(TouchableOpacity)<{ $isActive: boolean }>`
  padding: ${SpaceType.Tiny}px ${SpaceType.Small}px;
  border-radius: ${SpaceType.ExtraSmall}px;
  background-color: ${({
    $isActive,
    theme,
  }: {
    $isActive: boolean;
    theme: DefaultTheme;
  }) => ($isActive ? theme["option-bg-active"] : theme["option-bg-static"])};
`;

export default Breadcrumbs;
